import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { toast } from 'sonner';
import { Loader2 } from 'lucide-react';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '../ui/dialog';
import LmBulkDialog from '../list-management/LmBulkDialog';
import { errorText } from '../../lib/apiErrors';
import { createOwnerOperationsApi, useBackgroundReload, waitFor } from './ownerOperationsApi';

/**
 * Contact Owner requests (#863): the approver's and restorer's view on Contacts.
 *
 * - **Pending requests** (`contacts:override_owner`, admins): bulk and single
 *   Contact Owner changes waiting for a decision. Approve and Reject each need a
 *   reason; both are hidden on your own requests (the server refuses the
 *   requester either way, `403 approver_must_differ`). An approved request runs
 *   in the background and is followed to its outcome: rows changed since the
 *   request are conflicts.
 * - **Recent changes** (`contacts:restore_owner`, admins): the last 30 days'
 *   bulk and single changes, with **Undo** on the restorable ones.
 *
 * Both lists page by the server's `next_cursor` ("Load more").
 *
 * Migration approvals live in List Management → Import History, not here.
 */
const STATUS_LABEL = {
    committed: 'Committed', partially_committed: 'Partially committed', restoring: 'Restoring',
    partially_restored: 'Partially restored', restored: 'Restored', pending_approval: 'Waiting for approval',
};

function age(iso) {
    const t = iso ? new Date(iso).getTime() : NaN;
    if (Number.isNaN(t)) return '';
    const mins = Math.max(0, Math.round((Date.now() - t) / 60000));
    if (mins < 60) return `${mins} min ago`;
    const hours = Math.round(mins / 60);
    if (hours < 48) return `${hours} h ago`;
    return `${Math.round(hours / 24)} days ago`;
}

function useNames() {
    const names = useRef({});
    const remember = (results) => (results || []).forEach((r) => { if (r.name) names.current[r.row_id] = r.name; });
    const labelFor = (id) => names.current[id] || id;
    return { remember, labelFor };
}

/**
 * Undo one Contact Owner operation: a version-aware restore (admins, a reason
 * required). `count` is how many contacts it changed, when the caller knows.
 * Closed into the background, the restore is still running: `onDone(null,
 * { background: true })`, so the caller reloads again once it has finished.
 */
export function UndoOwnerChangeDialog({ open, onClose, operationId, count, getAuthHeader, onDone }) {
    const api = useMemo(() => createOwnerOperationsApi(getAuthHeader), [getAuthHeader]);
    const alive = useRef(true);
    const { remember, labelFor } = useNames();
    useEffect(() => () => { alive.current = false; }, []);
    useEffect(() => { if (open) alive.current = true; }, [open]);
    return (
        <LmBulkDialog
            open={open}
            onClose={onClose}
            title="Undo Contact Owner change"
            description="Puts back the previous Contact Owner of each contact this change moved. A contact changed again since keeps its current Contact Owner."
            confirmLabel="Undo change"
            tone="dark"
            askReason
            requireReason
            reasonPlaceholder="Why is this change being undone?"
            reasonMaxLength={500}
            background
            onBackground={() => onDone?.(null, { background: true })}
            selection={null}
            count={count}
            retryable={() => false}
            labelFor={labelFor}
            run={async (_sel, { reason, isAlive }) => {
                const started = await api.restore(operationId, reason);
                const view = await waitFor(api, started.restore_operation_id, { isAlive: () => alive.current && isAlive() });
                remember(view.results);
                return view;
            }}
            onDone={(res) => {
                if (res && res.failed === 0) toast.success('Contact Owner change undone');
                onDone?.(res);
            }}
        />
    );
}

function Rows({ api, operationId }) {
    const [state, setState] = useState({ loading: true });
    useEffect(() => {
        let alive = true;
        api.get(operationId).then((op) => alive && setState({ loading: false, rows: op.rows || [], to: op.new_owner_label }))
            .catch((err) => alive && setState({ loading: false, error: errorText(err, 'Could not load the contacts.') }));
        return () => { alive = false; };
    }, [api, operationId]);
    if (state.loading) return <Loader2 className="w-3.5 h-3.5 animate-spin text-stone-400" />;
    if (state.error) return <p role="alert" className="text-xs text-red-600">{state.error}</p>;
    return (
        <ul className="mt-1 max-h-[160px] overflow-y-auto text-xs text-stone-600 divide-y divide-stone-100" data-testid="owner-request-rows">
            {state.rows.map((r) => (
                <li key={r.contact_id} className="py-1">
                    <span className="font-medium text-stone-800">{r.name}</span>
                    {' · '}{r.current_owner_label || 'No Contact Owner'} → {state.to || 'new owner'}
                </li>
            ))}
        </ul>
    );
}

function OperationList({ api, status, render, emptyText, refreshKey }) {
    const [state, setState] = useState({ loading: true, operations: [] });
    const [more, setMore] = useState({ loading: false, error: null });
    // A reload (refreshKey) bumps the generation, so a page still in flight is dropped.
    const generation = useRef(0);
    useEffect(() => {
        const mine = ++generation.current;
        setState((s) => ({ ...s, loading: true, error: null }));
        setMore({ loading: false, error: null });
        api.list(status)
            .then((res) => mine === generation.current && setState({
                loading: false, operations: res.operations || [], total: res.total, cursor: res.next_cursor || null,
            }))
            .catch((err) => mine === generation.current
                && setState({ loading: false, operations: [], error: errorText(err, 'Could not load the list.') }));
        return () => { generation.current += 1; };
    }, [api, status, refreshKey]);
    const loadMore = () => {
        const mine = generation.current;
        setMore({ loading: true, error: null });
        api.list(status, { cursor: state.cursor })
            .then((res) => {
                if (mine !== generation.current) return;
                setState((s) => ({ ...s, operations: [...s.operations, ...(res.operations || [])], cursor: res.next_cursor || null }));
                setMore({ loading: false, error: null });
            })
            .catch((err) => mine === generation.current
                && setMore({ loading: false, error: errorText(err, 'Could not load more.') }));
    };
    if (state.loading) {
        return <div className="py-6 flex justify-center" role="status"><Loader2 className="w-4 h-4 animate-spin text-stone-400" /></div>;
    }
    if (state.error) return <p role="alert" className="py-4 text-sm text-red-600">{state.error}</p>;
    if (!state.operations.length) return <p className="py-6 text-center text-sm text-stone-500">{emptyText}</p>;
    return (
        <div>
            <ul className="divide-y divide-stone-100">{state.operations.map(render)}</ul>
            {more.error && <p role="alert" className="pt-2 text-center text-sm text-red-600">{more.error}</p>}
            {state.cursor && (
                <div className="pt-2 flex justify-center">
                    <button type="button" onClick={loadMore} disabled={more.loading} data-testid="owner-operations-more"
                            className="h-8 px-3 rounded-lg border border-stone-200 text-sm text-stone-700 hover:bg-stone-50 disabled:opacity-50">
                        {more.loading ? 'Loading…' : more.error ? 'Retry' : 'Load more'}
                    </button>
                </div>
            )}
        </div>
    );
}

export default function OwnerRequestsDialog({ open, onClose, getAuthHeader, currentUserId, canApprove, canRestore, onChanged }) {
    const api = useMemo(() => createOwnerOperationsApi(getAuthHeader), [getAuthHeader]);
    const [tab, setTab] = useState(canApprove ? 'pending' : 'recent');
    const [shown, setShown] = useState(null);
    const [decision, setDecision] = useState(null); // {op, kind: 'approve' | 'reject'}
    const [undo, setUndo] = useState(null); // the Recent changes entry being undone
    const [refreshKey, setRefreshKey] = useState(0);
    const alive = useRef(true);
    const { remember, labelFor } = useNames();
    useEffect(() => () => { alive.current = false; }, []);
    useEffect(() => { if (open) { alive.current = true; setTab(canApprove ? 'pending' : 'recent'); } }, [open, canApprove]);

    // An approval or Undo closed into the background (`{ background: true }`) is
    // still running: the list and `onChanged` reload again once it has finished.
    const reloadList = useBackgroundReload(() => setRefreshKey((k) => k + 1));
    const refresh = useCallback((opts) => { reloadList(opts); onChanged?.(opts); }, [reloadList, onChanged]);

    const pendingRow = (op) => {
        const mine = op.requested_by?.id === currentUserId;
        const counts = op.preview_counts || {};
        return (
            <li key={op.operation_id} className="py-3" data-testid="owner-request">
                <div className="flex items-start gap-3">
                    <div className="flex-1 min-w-0 text-sm">
                        <div className="text-stone-900">
                            <span className="font-medium">{op.requested_by?.name || 'A teammate'}</span>
                            {' asks to move '}{counts.changed ?? 0} contact{counts.changed === 1 ? '' : 's'}
                            {' to '}<span className="font-medium">{op.new_owner_label || 'a new owner'}</span>
                        </div>
                        <div className="text-xs text-stone-500 mt-0.5">
                            {op.request_reason || op.reason || 'No reason given'} · {age(op.requested_at)}
                        </div>
                        <button type="button" className="mt-1 text-xs text-stone-600 underline"
                                onClick={() => setShown(shown === op.operation_id ? null : op.operation_id)}>
                            {shown === op.operation_id ? 'Hide contacts' : 'Show contacts'}
                        </button>
                        {shown === op.operation_id && <Rows api={api} operationId={op.operation_id} />}
                    </div>
                    <div className="flex gap-2 shrink-0">
                        {mine ? (
                            <span className="text-xs text-stone-500 self-center" data-testid="owner-request-mine">Your request</span>
                        ) : (
                            <>
                                <button type="button" onClick={() => setDecision({ op, kind: 'reject' })} data-testid="owner-request-reject"
                                        className="h-8 px-3 rounded-lg border border-stone-200 text-sm text-stone-700 hover:bg-stone-50">Reject</button>
                                <button type="button" onClick={() => setDecision({ op, kind: 'approve' })} data-testid="owner-request-approve"
                                        className="h-8 px-3 rounded-lg bg-stone-900 text-white text-sm hover:bg-stone-800">Approve</button>
                            </>
                        )}
                    </div>
                </div>
            </li>
        );
    };

    const recentRow = (op) => {
        const counts = op.counts || {};
        return (
            <li key={op.operation_id} className="py-3 flex items-start gap-3" data-testid="owner-recent">
                <div className="flex-1 min-w-0 text-sm">
                    <div className="text-stone-900">
                        {counts.changed ?? 0} contact{counts.changed === 1 ? '' : 's'} moved to{' '}
                        <span className="font-medium">{op.new_owner_label || 'a new owner'}</span>
                        {' by '}{op.requested_by?.name || 'a teammate'}
                        {op.approval?.approver_name ? `, approved by ${op.approval.approver_name}` : ''}
                    </div>
                    <div className="text-xs text-stone-500 mt-0.5">
                        {STATUS_LABEL[op.status] || op.status} · {op.reason || op.request_reason || 'No reason given'} · {age(op.requested_at)}
                    </div>
                </div>
                {op.restorable && (
                    <button type="button" onClick={() => setUndo(op)} data-testid="owner-recent-undo"
                            className="h-8 px-3 rounded-lg border border-stone-200 text-sm text-stone-700 hover:bg-stone-50 shrink-0">Undo</button>
                )}
            </li>
        );
    };

    const tabs = [canApprove && ['pending', 'Pending requests'], canRestore && ['recent', 'Recent changes']].filter(Boolean);
    return (
        <Dialog open={open} onOpenChange={(v) => { if (!v) onClose?.(); }}>
            <DialogContent className="sm:max-w-[640px]" data-testid="owner-requests-dialog">
                <DialogHeader>
                    <DialogTitle className="text-[17px] font-semibold text-stone-900">Contact Owner requests</DialogTitle>
                    <DialogDescription className="text-[13px] text-stone-500">
                        Contact Owner changes waiting for an admin, and recent changes you can undo.
                    </DialogDescription>
                </DialogHeader>
                {tabs.length > 1 && (
                    <div className="flex gap-2 border-b border-stone-100" role="tablist">
                        {tabs.map(([key, label]) => (
                            <button key={key} type="button" role="tab" aria-selected={tab === key} onClick={() => setTab(key)}
                                    className={`px-3 py-2 text-sm -mb-px border-b-2 ${tab === key ? 'border-stone-900 text-stone-900' : 'border-transparent text-stone-500'}`}>
                                {label}
                            </button>
                        ))}
                    </div>
                )}
                {tab === 'pending' && canApprove && (
                    <OperationList api={api} status="pending_approval" render={pendingRow} refreshKey={refreshKey}
                                   emptyText="No Contact Owner requests are waiting." />
                )}
                {tab === 'recent' && canRestore && (
                    <OperationList api={api} status="recent" render={recentRow} refreshKey={refreshKey}
                                   emptyText="No Contact Owner changes in the last 30 days" />
                )}
                <LmBulkDialog
                    open={!!decision}
                    onClose={() => setDecision(null)}
                    title={decision?.kind === 'approve' ? 'Approve Contact Owner change' : 'Reject Contact Owner change'}
                    description={decision?.kind === 'approve'
                        ? 'Contacts changed by someone else since the request keep their current Contact Owner.'
                        : 'The requester is told, with your reason. Nothing changes.'}
                    confirmLabel={decision?.kind === 'approve' ? 'Approve' : 'Reject'}
                    tone="dark"
                    askReason
                    requireReason
                    reasonPlaceholder="Reason (the requester sees it)"
                    reasonMaxLength={500}
                    background={decision?.kind === 'approve'}
                    onBackground={() => refresh({ background: true })}
                    selection={null}
                    count={decision?.op?.preview_counts?.changed ?? 1}
                    retryable={() => false}
                    labelFor={labelFor}
                    run={async (_sel, { reason, isAlive }) => {
                        const { op, kind } = decision;
                        if (kind === 'reject') {
                            await api.reject(op.operation_id, reason);
                            return { succeeded: 1, failed: 0, results: [] };
                        }
                        await api.approve(op.operation_id, reason);
                        const view = await waitFor(api, op.operation_id, { isAlive: () => alive.current && isAlive() });
                        remember(view.results);
                        return view;
                    }}
                    onDone={(res) => {
                        if (res && res.failed === 0) toast.success(decision?.kind === 'approve' ? 'Request approved' : 'Request rejected');
                        refresh();
                    }}
                />
                <UndoOwnerChangeDialog open={!!undo} operationId={undo?.operation_id} count={undo?.counts?.changed}
                                       getAuthHeader={getAuthHeader} onClose={() => setUndo(null)}
                                       onDone={(_res, opts) => refresh(opts)} />
            </DialogContent>
        </Dialog>
    );
}
